import { useEffect, useRef, useState } from "react";
import type { MedicationSetting } from "@/domain/entities";
import { TrashIcon } from "../components/TrashIcon";
import { useLocale } from "../i18n/LocaleProvider";
import styles from "./SettingsScreen.module.css";

interface Props {
  saved: MedicationSetting[];
  onSave: (medicines: MedicationSetting[]) => Promise<void>;
}

interface Draft {
  name: string;
  defaultDose: string;
}

const toDraft = (m: MedicationSetting): Draft => ({ name: m.name, defaultDose: m.defaultDose ?? "" });

const toMedicines = (rows: Draft[]): MedicationSetting[] =>
  rows
    .filter((row) => row.name.trim() !== "")
    .map((row) => {
      const medicine: MedicationSetting = { name: row.name.trim() };
      if (row.defaultDose.trim()) medicine.defaultDose = row.defaultDose.trim();
      return medicine;
    });

/**
 * The scenario's own medicines. Like every other setting they save by themselves:
 * a field when it loses focus, a removal at once. Rows without a name are not kept.
 */
export function MedicinesEditor({ saved, onSave }: Props) {
  const { t } = useLocale();
  const [rows, setRows] = useState<Draft[]>(saved.map(toDraft));
  const serialized = JSON.stringify(saved);
  const lastSaved = useRef(serialized);

  // A change from elsewhere (a moved diary, a restored archive) replaces the drafts. Keyed on the value, the
  // effect skips renders that still carry the value from before our own save, and our save coming back is equal.
  useEffect(() => {
    if (serialized === lastSaved.current) return;
    lastSaved.current = serialized;
    setRows((JSON.parse(serialized) as MedicationSetting[]).map(toDraft));
  }, [serialized]);

  const commit = (next: Draft[]) => {
    const medicines = toMedicines(next);
    const serialized = JSON.stringify(medicines);
    if (serialized === lastSaved.current) return;
    lastSaved.current = serialized;
    void onSave(medicines);
  };
  const patch = (index: number, change: Partial<Draft>) => setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...change } : row)));
  const remove = (index: number) => {
    const next = rows.filter((_, i) => i !== index);
    setRows(next);
    commit(next);
  };

  return (
    <div className={styles.subsection}>
      <span className={styles.label}>{t.settings.medicines}</span>
      {rows.map((row, index) => (
        <div key={index} className={styles.medicine}>
          <label>{t.settings.medicineName}<input className={styles.field} value={row.name} onChange={(e) => patch(index, { name: e.target.value })} onBlur={() => commit(rows)} /></label>
          <label>{t.settings.defaultDose}<input className={styles.field} value={row.defaultDose} onChange={(e) => patch(index, { defaultDose: e.target.value })} onBlur={() => commit(rows)} /></label>
          <button type="button" className={styles.iconButton} aria-label={`${t.common.remove}: ${row.name.trim() || t.settings.medicineName}`} onClick={() => remove(index)}>
            <TrashIcon size={20} />
          </button>
        </div>
      ))}
      <button type="button" className={styles.textButton} onClick={() => setRows((prev) => [...prev, { name: "", defaultDose: "" }])}>+ {t.settings.addMedicine}</button>
    </div>
  );
}
